import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { verifyWalletFund, getWalletDashboard, getWalletFundOptions } from '../../redux/buzzbridge/actions';
import type { IVerifyWalletFundResult } from '../../redux/buzzbridge/types';
import { useDispatch } from 'react-redux';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import {
    clearPaystackFundReference,
    clearWalletFundReturnUrl,
    readPaystackFundReference,
    readWalletFundCheckoutProvider,
    readWalletFundReturnUrl,
} from './walletFundRailUtils';
import { isFlutterwaveAbandonedReturn } from '../../utils/flutterwaveReturn';

function resolveWalletFundDestination(): string {
    const returnUrl = readWalletFundReturnUrl();
    clearWalletFundReturnUrl();
    if (returnUrl && returnUrl.startsWith('/')) return returnUrl;
    return ROUTES.BRAND_WALLET;
}

function pickPaystackReference(searchParams: URLSearchParams): string | null {
    return searchParams.get('reference') ?? searchParams.get('trxref') ?? readPaystackFundReference();
}

function pickFlutterwavePayload(searchParams: URLSearchParams): {
    transaction_id?: string;
    tx_ref?: string;
    reference?: string;
} | null {
    const transaction_id = searchParams.get('transaction_id') ?? undefined;
    const tx_ref =
        searchParams.get('tx_ref') ?? searchParams.get('reference') ?? readPaystackFundReference() ?? undefined;
    if (!transaction_id && !tx_ref) return null;
    return { transaction_id, tx_ref, reference: tx_ref };
}

function isVerified(result: IVerifyWalletFundResult | null | undefined): boolean {
    if (!result) return false;
    if (result.verified === true) return true;
    return result.transaction?.status === 'completed';
}

export default function BrandWalletFundCallbackPage() {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [searchParams] = useSearchParams();
    const retried = useRef(false);
    const [status, setStatus] = useState<'verifying' | 'success' | 'pending' | 'error'>('verifying');
    const [message, setMessage] = useState('Confirming your payment…');

    const finishSuccess = useCallback(() => {
        const creditLabel = sessionStorage.getItem('campaign_wallet_fund_credit_label');
        clearPaystackFundReference();
        try {
            sessionStorage.removeItem('campaign_wallet_fund_credit_label');
        } catch {
            /* ignore */
        }
        const destination = resolveWalletFundDestination();
        const returningToCampaign = destination !== ROUTES.BRAND_WALLET;
        dispatch(getWalletDashboard());
        dispatch(getWalletFundOptions());
        showToastMessage(creditLabel ? `${creditLabel} added to your wallet` : 'Funds added to your wallet', 'success');
        setStatus('success');
        setMessage(
            returningToCampaign
                ? 'Payment confirmed. Taking you back to your campaign…'
                : 'Payment confirmed. Redirecting to your wallet…',
        );
        setTimeout(() => navigate(destination, { replace: true }), 1500);
    }, [dispatch, navigate]);

    useEffect(() => {
        if (isFlutterwaveAbandonedReturn(searchParams)) {
            navigate(resolveWalletFundDestination(), { replace: true });
            return;
        }
        const useFlutterwave =
            Boolean(searchParams.get('transaction_id') || searchParams.get('tx_ref')) ||
            readWalletFundCheckoutProvider() === 'flutterwave';

        const verify = async (retry: boolean) => {
            try {
                let result: IVerifyWalletFundResult;
                if (useFlutterwave) {
                    const fw = pickFlutterwavePayload(searchParams);
                    if (!fw) {
                        setStatus('error');
                        setMessage('Missing payment reference. Return to the wallet and try again.');
                        return;
                    }
                    result = (await dispatchPromise(
                        verifyWalletFund({ ...fw, provider: 'flutterwave' }),
                    )) as IVerifyWalletFundResult;
                } else {
                    const reference = pickPaystackReference(searchParams);
                    if (!reference) {
                        setStatus('error');
                        setMessage('Missing payment reference. Return to the wallet and try again.');
                        return;
                    }
                    result = (await dispatchPromise(
                        verifyWalletFund({ reference, provider: 'paystack' }),
                    )) as IVerifyWalletFundResult;
                }

                if (isVerified(result)) {
                    finishSuccess();
                    return;
                }

                if (!retry && !retried.current) {
                    retried.current = true;
                    setMessage('Payment processing… checking again in a moment.');
                    await new Promise(r => setTimeout(r, 2500));
                    await verify(true);
                    return;
                }

                setStatus('pending');
                setMessage(
                    'Payment received but still processing. Your wallet will update shortly — refresh the wallet page in a few seconds.',
                );
            } catch (err: unknown) {
                const msg =
                    (err as { data?: { message?: string } })?.data?.message ??
                    (err as Error)?.message ??
                    'Could not verify payment.';
                setStatus('error');
                setMessage(msg);
            }
        };

        void verify(false);
    }, [searchParams, dispatchPromise, finishSuccess, navigate]);

    return (
        <MetaTags title="Wallet payment | Brand | Thspian" description="Confirming wallet deposit">
            <Box
                sx={{
                    minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: 3,
                    textAlign: 'center',
                }}>
                {status === 'verifying' ? <CircularProgress sx={{ color: t.primary_orange }} /> : null}
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: t.primary_white }}>
                    {status === 'success' ? 'Payment successful' : 'Wallet deposit'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: t.form_label, maxWidth: 420, lineHeight: 1.5 }}>
                    {message}
                </Typography>
                {(status === 'error' || status === 'pending') && (
                    <Button
                        variant="contained"
                        onClick={() => navigate(resolveWalletFundDestination(), { replace: true })}
                        sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            bgcolor: t.primary_orange,
                        }}>
                        Continue
                    </Button>
                )}
            </Box>
        </MetaTags>
    );
}
